// Filters that stay put (client, 2026-10-03): "a filter I set must not go away
// when I open a record and come back — only when I reset it."
//
// Kept PER USER ON THE SERVER (client, 2026-10-06: the same admin saw different
// data on a laptop and a desktop, because each browser remembered its own
// filters / dashboard tab). The server copy (user.uiPrefs, loaded with
// /api/auth/me) wins, so every computer opens the same view; this browser's
// localStorage is only a fallback (offline, or before the first save).
// Resetting a filter back to its default removes it. Every read/write is
// guarded — storage or network trouble just means the page starts with its
// defaults.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/shared/api/axios';
import { useUser } from './useAuth';

const PREFIX = 'rbm.filters';

function storageKey(userId: string | undefined, page: string) {
  return `${PREFIX}.${userId || 'anon'}.${page}`;
}

function readAll(key: string): Record<string, unknown> {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function writeLocal(key: string, field: string, value: unknown, isDefault: boolean) {
  try {
    const all = readAll(key);
    if (isDefault) delete all[field]; else all[field] = value;
    if (Object.keys(all).length) window.localStorage.setItem(key, JSON.stringify(all));
    else window.localStorage.removeItem(key);
  } catch {
    // storage unavailable — the filter still works, it just isn't remembered here
  }
}

// One pending save per page.field, so typing in a search box sends one request.
const pending = new Map<string, ReturnType<typeof setTimeout>>();
// Changes made in this session — the user object loaded at login does not see
// them, so they are checked first when a page opens again.
const sessionChanges = new Map<string, unknown>();
function saveToServer(userId: string | undefined, page: string, field: string, value: unknown) {
  const k = `${userId || "anon"}.${page}.${field}`;
  sessionChanges.set(k, value);
  const t = pending.get(k);
  if (t) clearTimeout(t);
  pending.set(k, setTimeout(() => {
    pending.delete(k);
    api.put('/api/auth/me/ui-prefs', { page, field, value }).catch(() => { /* kept locally anyway */ });
  }, 700));
}

/** The server's saved value for page.field, or undefined. */
function serverValue(user: ReturnType<typeof useUser>, page: string, field: string): unknown {
  const k = `${user?.id || "anon"}.${page}.${field}`;
  if (sessionChanges.has(k)) { const c = sessionChanges.get(k); return c === null ? undefined : c; }
  const prefs = (user as { uiPrefs?: Record<string, Record<string, unknown>> } | null)?.uiPrefs;
  const v = prefs?.[page]?.[field];
  return v === null ? undefined : v;
}

/**
 * Drop-in for useState for one filter on a list page. `page` names the list
 * (e.g. 'accounts-invoices'), `field` the filter on it.
 */
export function useRememberedState<T>(page: string, field: string, initial: T) {
  const user = useUser();
  const key = storageKey(user?.id, page);
  const [value, setValue] = useState<T>(() => {
    const fromServer = serverValue(user, page, field);
    if (fromServer !== undefined) return fromServer as T;
    const saved = readAll(key)[field];
    return saved === undefined ? initial : (saved as T);
  });
  const first = useRef(true);
  useEffect(() => {
    const isDefault = JSON.stringify(value) === JSON.stringify(initial);
    writeLocal(key, field, value, isDefault);
    // The first run only mirrors what was loaded — no need to write it back.
    if (first.current) { first.current = false; return; }
    saveToServer(user?.id, page, field, isDefault ? null : value);
    // `initial` is a constant default per call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, field, value]);
  return [value, setValue] as const;
}

/**
 * For pages that keep their filters in the URL (?stage=…&owner=…): returns the
 * params to start from — the URL's own when it has any (a shared link, a
 * dashboard tile), otherwise the ones this user last left on the page — and a
 * `remember` function to call with the live params whenever they change.
 * Keys in `exclude` (one-off link parameters) are never remembered.
 */
export function useRememberedSearchParams(page: string, current: URLSearchParams, exclude: string[] = []) {
  const user = useUser();
  const key = storageKey(user?.id, page);
  const seed = useMemo(() => {
    const hasOwn = [...current.keys()].some((k) => !exclude.includes(k));
    if (hasOwn) return new URLSearchParams(current);
    const fromServer = serverValue(user, page, 'query');
    const saved = fromServer !== undefined ? fromServer : readAll(key).query;
    const restored = new URLSearchParams(typeof saved === 'string' ? saved : '');
    for (const [k, v] of current) restored.set(k, v);
    return restored;
    // Only the params the page opened with matter for the seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const last = useRef<string | null>(null);
  const remember = (live: URLSearchParams) => {
    const keep = new URLSearchParams(live);
    for (const k of exclude) keep.delete(k);
    const query = keep.toString();
    writeLocal(key, 'query', query, query === '');
    if (last.current === null) { last.current = query; return; } // first mirror after load
    if (query === last.current) return;
    last.current = query;
    saveToServer(user?.id, page, 'query', query === '' ? null : query);
  };
  return { seed, remember };
}
